import { memo } from 'react';
import { FilePen, Trash2 } from 'lucide-react';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { Tooltip, TooltipTrigger, TooltipContent } from '@reflex/ui/components/ui/tooltip';
import { Button } from '@reflex/ui/components/ui/button';
import { SidebarItem } from '@reflex/ui/components/sidebar/SidebarItem';
import type { MenuActionItem } from '@reflex/ui/components/sidebar/SidebarItem';
import { timeAgo } from '@reflex/ui/lib/format';
import { generateFallbackTitle } from '@reflex/shared';
import { AgentMetaLine } from '@/components/agent/AgentMetaLine';
import { getDraftMeta } from '@/components/agent/draft-meta';
import type { AgentDraft } from '@/lib/types';

export interface DraftAgentRowProps {
  draft: AgentDraft;
  selected: boolean;
  onSelect: (draft: AgentDraft) => void;
  onDelete: (draft: AgentDraft) => void;
}

export const DraftAgentRow = memo(function DraftAgentRow({
  draft,
  selected,
  onSelect,
  onDelete,
}: DraftAgentRowProps) {
  const title = generateFallbackTitle(
    (draft.payload.initialPrompt ?? draft.payload.prompt ?? '').trim(),
  );
  const draftMeta = getDraftMeta(draft);

  const menuActions: MenuActionItem[] = [
    {
      label: 'Delete draft',
      icon: Trash2,
      onClick: () => onDelete(draft),
      destructive: true,
    },
  ];

  return (
    <SidebarItem
      selected={selected}
      onClick={() => onSelect(draft)}
      testId={`agent-draft-row-${draft.id}`}
      icon={<FilePen className="icon-md text-muted-foreground" />}
      // No left accent: the "Drafts" section label and the FilePen icon
      // already mark these rows as unstarted work, and a per-row rule read as
      // one long dashed line down the whole section.
      actions={
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Delete draft"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => onDelete(draft)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Delete draft</TooltipContent>
        </Tooltip>
      }
      menuActions={menuActions}
    >
      <div className="flex items-center justify-between gap-2">
        <PanelTitle as="h3" size="sm" className="min-w-0 flex-1 font-normal">
          {title}
        </PanelTitle>
        <span className="shrink-0 text-3xs text-muted-foreground">
          {timeAgo(draft.updatedAt, { compact: true })}
        </span>
      </div>
      {draftMeta.agentType ? (
        <AgentMetaLine
          agentType={draftMeta.agentType}
          model={draftMeta.model}
          repoSlug={draftMeta.repoSlug}
          branch={draftMeta.branch}
        />
      ) : null}
    </SidebarItem>
  );
});
